import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import {
  initializeSquare,
  createPaymentForm,
  processPayment,
  PaymentResult,
  handleSquareError,
  validatePaymentAmount,
} from '../../secret/lib/square'

interface PaymentMethods {
  card: any
  applePay: any
  googlePay: any
  payments: any
}

interface DonationPaymentContextType {
  // Payment methods
  paymentMethods: PaymentMethods | null

  // Payment state
  isProcessing: boolean
  paymentError: string | null

  // Methods
  initializePayment: (containerId: string, amount: number) => Promise<void>
  processCardPayment: (amount: number, donationId: string) => Promise<PaymentResult>
  processApplePayment: (amount: number, donationId: string) => Promise<PaymentResult>
  processGooglePayment: (amount: number, donationId: string) => Promise<PaymentResult>
  clearError: () => void
  setError: (error: string) => void

  // Utilities
  isApplePayAvailable: boolean
  isGooglePayAvailable: boolean
}

const DonationPaymentContext = createContext<DonationPaymentContextType | null>(null)

export const useDonationPayment = () => {
  const context = useContext(DonationPaymentContext)
  if (!context) {
    throw new Error('useDonationPayment must be used within a DonationPaymentProvider')
  }
  return context
}

export const DonationPaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // No auth required for donations - they are anonymous
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethods | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false)
  const [isGooglePayAvailable, setIsGooglePayAvailable] = useState(false)
  const cardRef = useRef<any>(null)

  // Initialize Square SDK on component mount
  useEffect(() => {
    initializeSquare().catch(error => {
      console.error('Failed to initialize Square SDK:', error)
      setPaymentError('Payment system unavailable. Please try again later.')
    })

    return () => {
      if (cardRef.current) {
        cardRef.current.destroy().catch((error: any) => {
          console.error('Error destroying card on unmount:', error)
        })
      }
    }
  }, [])

  const initializePayment = useCallback(async (containerId: string, amount: number) => {
    try {
      setPaymentError(null)

      if (!validatePaymentAmount(amount)) {
        throw new Error('Invalid payment amount')
      }

      // Destroy existing card instance before creating new one
      if (cardRef.current) {
        await cardRef.current.destroy()
        cardRef.current = null
      }

      const methods = await createPaymentForm(containerId, amount, 'Tailtations Donation')
      cardRef.current = methods.card
      setPaymentMethods(methods)

      // Check availability of digital wallets
      // Apple Pay works in Safari, Google Pay works in Chrome/Chromium browsers
      const isChromium = !!(window as any).chrome || navigator.userAgent.includes('Chrome')
      setIsApplePayAvailable(!!methods.applePay)
      setIsGooglePayAvailable(!!methods.googlePay && isChromium)

    } catch (error) {
      console.error('Payment initialization failed:', error)
      setPaymentError('Failed to initialize payment form. Please refresh and try again.')
    }
  }, [])

  // Tokenize payment - donations don't call backend directly from context
  // The DonatePage handles the backend call with donation-specific data
  const processCardPayment = async (amount: number, donationId: string): Promise<PaymentResult> => {
    if (!paymentMethods?.card) {
      throw new Error('Card payment method not initialized')
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      // Tokenize payment method
      const tokenResult = await processPayment(paymentMethods.card, amount, donationId)

      if (tokenResult.errors) {
        const errorMessage = handleSquareError(tokenResult.errors)
        setPaymentError(errorMessage)
        return tokenResult
      }

      // Return token for DonatePage to process via donation-specific endpoint
      return tokenResult
    } catch (error: any) {
      const errorMessage = error.message || 'Payment failed. Please try again.'
      setPaymentError(errorMessage)
      return { errors: [{ category: 'PAYMENT_ERROR', code: 'PROCESSING_ERROR', detail: errorMessage }] }
    } finally {
      setIsProcessing(false)
    }
  }

  const processApplePayment = async (amount: number, donationId: string): Promise<PaymentResult> => {
    if (!paymentMethods?.applePay) {
      throw new Error('Apple Pay not available')
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      // Tokenize payment method
      const tokenResult = await processPayment(paymentMethods.applePay, amount, donationId)

      if (tokenResult.errors) {
        const errorMessage = handleSquareError(tokenResult.errors)
        setPaymentError(errorMessage)
        return tokenResult
      }

      // Return token for DonatePage to process via donation-specific endpoint
      return tokenResult
    } catch (error: any) {
      const errorMessage = error.message || 'Apple Pay failed. Please try again.'
      setPaymentError(errorMessage)
      return { errors: [{ category: 'PAYMENT_ERROR', code: 'PROCESSING_ERROR', detail: errorMessage }] }
    } finally {
      setIsProcessing(false)
    }
  }

  const processGooglePayment = async (amount: number, donationId: string): Promise<PaymentResult> => {
    if (!paymentMethods?.googlePay) {
      throw new Error('Google Pay not available')
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      // Tokenize payment method
      const tokenResult = await processPayment(paymentMethods.googlePay, amount, donationId)

      if (tokenResult.errors) {
        const errorMessage = handleSquareError(tokenResult.errors)
        setPaymentError(errorMessage)
        return tokenResult
      }

      // Return token for DonatePage to process via donation-specific endpoint
      return tokenResult
    } catch (error: any) {
      const errorMessage = error.message || 'Google Pay failed. Please try again.'
      setPaymentError(errorMessage)
      return { errors: [{ category: 'PAYMENT_ERROR', code: 'PROCESSING_ERROR', detail: errorMessage }] }
    } finally {
      setIsProcessing(false)
    }
  }

  const clearError = () => {
    setPaymentError(null)
  }

  const setError = (error: string) => {
    setPaymentError(error)
  }

  return (
    <DonationPaymentContext.Provider
      value={{
        paymentMethods,
        isProcessing,
        paymentError,
        initializePayment,
        processCardPayment,
        processApplePayment,
        processGooglePayment,
        clearError,
        setError,
        isApplePayAvailable,
        isGooglePayAvailable
      }}
    >
      {children}
    </DonationPaymentContext.Provider>
  )
}

export default DonationPaymentProvider
